import React, { useState, useCallback, useEffect } from 'react';
import { useDropzone } from 'react-dropzone';
import { Upload, FileText, X, CheckCircle } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import './ContextUpload.css';

const ContextUpload = ({ contextData, setContextData, onFilesChange, showFilesOnly = false, showTextOnly = false }) => {
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  // Use local state for textarea to avoid conflicts with App.js loading
  const [localContextData, setLocalContextData] = useState('');
  const [originalContextData, setOriginalContextData] = useState(null);
  const [originalUploadedFiles, setOriginalUploadedFiles] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [saveProgress, setSaveProgress] = useState(0);
  const [saveStatus, setSaveStatus] = useState('');
  const { user, getToken } = useAuth();

  // Watch for contextData prop changes and update local state
  useEffect(() => {
    if (contextData && contextData.trim() && contextData !== localContextData) {
      console.log('ContextUpload: contextData prop changed, updating local state:', contextData);
      setLocalContextData(contextData);
      setOriginalContextData(contextData);
    }
  }, [contextData]);

  // Load existing context when component mounts
  useEffect(() => {
    const loadExistingContext = async () => {
      if (!user) return;
      
      try {
        setIsLoading(true);
        
        const token = await getToken();
        if (!token) return;
        
        // Get uploaded files first
        const filesResponse = await fetch('/api/agents/files', {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (filesResponse.ok) {
          const filesData = await filesResponse.json();
          
          if (filesData.files && Array.isArray(filesData.files)) {
            setUploadedFiles(filesData.files);
            setOriginalUploadedFiles(filesData.files);
            // Call onFilesChange to notify parent component about loaded files
            const nonWebpageFiles = filesData.files.filter(f => f.source !== 'website_scan');
            onFilesChange(nonWebpageFiles);
          } else {
            setUploadedFiles([]);
            setOriginalUploadedFiles([]);
            onFilesChange([]);
          }
        } else {
          setUploadedFiles([]);
          setOriginalUploadedFiles([]);
          onFilesChange([]);
        }
        
        // Load context from API (App.js will also load it, but we need it here too)
        // The separate useEffect above will sync when App.js's contextData prop arrives
        const response = await fetch('/api/agents', {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (response.ok) {
          const agentData = await response.json();
          if (agentData.agent) {
            // API returns builtInContext (camelCase), not built_in_context (snake_case)
            const loadedContext = agentData.agent.builtInContext || agentData.agent.built_in_context || '';
            console.log('ContextUpload: Loaded context data from API:', loadedContext);
            console.log('ContextUpload: Full agent data:', agentData.agent);
            
            // Only set if local state is still empty (App.js might set it via prop)
            setTimeout(() => {
              if (!localContextData || localContextData.trim() === '') {
                console.log('ContextUpload: Setting textarea content from API (last step):', loadedContext);
                setLocalContextData(loadedContext);
                setOriginalContextData(loadedContext);
              } else {
                console.log('ContextUpload: Local state already has data, skipping API value');
              }
            }, 200);
          }
        }
      } catch (error) {
        console.error('Error loading existing context:', error);
      } finally {
        // Set loading to false after a delay to ensure textarea is updated
        setTimeout(() => {
          setIsLoading(false);
        }, 150);
      }
    };

    loadExistingContext();
  }, [user, setContextData]); // Removed onFilesChange from dependencies to prevent unnecessary calls

  const onDrop = useCallback(async (acceptedFiles) => {
    // Only allow text files
    const textFiles = acceptedFiles.filter(file => 
      file.type === 'text/plain' || file.name.endsWith('.txt') || file.name.endsWith('.md')
    );
  
    if (textFiles.length !== acceptedFiles.length) {
      alert('Only text files (.txt, .md) are supported. Other file types will be ignored.');
    }
  
    const newFiles = [];
    for (const file of textFiles) {
      try {
        // Keep content for DB
        const content = await file.text();
  
        // Compute hash safely from ArrayBuffer
        const arrayBuffer = await file.arrayBuffer();
        await computeFileHash(arrayBuffer);
  
        newFiles.push({
          id: Date.now() + Math.random(),
          file,             // <-- keep the original File object here
          name: file.name,
          size: file.size,
          type: file.type,
          content: content   // optional duplicate for preview or database
        });
      } catch (error) {
        console.error('Error reading file:', file.name, error);
        alert(`Error reading file ${file.name}. Please try again.`);
      }
    }
  
    const updatedFiles = [...uploadedFiles, ...newFiles];
    setUploadedFiles(updatedFiles);
    setHasUnsavedChanges(true);
  
    // Notify parent component about the file changes
    onFilesChange?.(updatedFiles.filter(f => f.source !== 'website_scan'));
  }, [uploadedFiles, onFilesChange]);
  

  // Save context function (currently unused)
  /*
  const saveContext = useCallback(async (context) => {
    console.log('saveContext called with:', context);
    console.log('user object:', user);
    
    if (!user?.agent?.id) {
      console.log('No agent ID found, user.agent:', user?.agent);
      return;
    }
    
    try {
      // Send all required fields along with the new context
      const updateData = {
        name: user.agent.name || 'My Agent',
        description: user.agent.description || 'AI agent built with Agent Builder',
        systemPrompt: user.agent.systemPrompt || 'You are a helpful AI assistant.',
        builtInContext: context
      };
      
      console.log('Calling PUT /api/agents with:', updateData);
      
      const token = await getToken();
      if (!token) throw new Error('No auth token found');
      
      const response = await fetch(`/api/agents`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(updateData)
      });
      
      console.log('Response status:', response.status);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('Response error:', errorText);
        throw new Error('Failed to save context');
      }
      
      console.log('Context saved successfully');
    } catch (error) {
      console.error('Error saving context:', error);
      throw error;
    }
  }, [user]);
  */

  // Save text input when it changes - REMOVED: We now only save when user clicks Save Changes
  // useEffect(() => {
  //   const timeoutId = setTimeout(() => {
  //     if (contextData !== undefined) {
  //       saveContext(contextData);
  //     }
  //   }, 1000);

  //   return () => clearTimeout(timeoutId);
  // }, [contextData, saveContext]);

  // Track changes for unsaved changes indicator
  useEffect(() => {
    // Only check for changes if we have loaded the original data
    if (originalContextData === null || originalUploadedFiles === null) {
      return;
    }
    
    const hasChanges = 
      localContextData !== originalContextData ||
      JSON.stringify(uploadedFiles.filter(f => f.source !== 'website_scan').map(f => ({ id: f.id, name: f.name, content: f.content }))) !== 
      JSON.stringify(originalUploadedFiles.filter(f => f.source !== 'website_scan').map(f => ({ id: f.id, name: f.name, content: f.content })));
    
    setHasUnsavedChanges(hasChanges);
  }, [localContextData, uploadedFiles, originalContextData, originalUploadedFiles]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'text/*': ['.txt', '.md']
    },
    multiple: true
  });

  const removeFile = (fileId) => {
    // Remove the file locally
    const updatedFiles = uploadedFiles.filter(f => f.id !== fileId);
    setUploadedFiles(updatedFiles);
    setHasUnsavedChanges(true);
  
    // Notify parent
    onFilesChange?.(updatedFiles.filter(f => f.source !== 'website_scan'));
  };
  

  const computeFileHash = async (file) => {
    if (!file) return null; // return early if file is missing
    const f = file.file || file; // support {file} wrapper or File object
    if (!f || !f.size) return null;
    
    // Check if the file object has arrayBuffer method (is a proper File/Blob)
    if (typeof f.arrayBuffer !== 'function') {
      // If it's not a File/Blob object, try to create a hash from the content
      if (file.content && typeof file.content === 'string') {
        const encoder = new TextEncoder();
        const data = encoder.encode(file.content);
        const hashBuffer = await crypto.subtle.digest('SHA-256', data);
        return Array.from(new Uint8Array(hashBuffer)).map(b => b.toString(16).padStart(2, '0')).join('');
      }
      // If no content available, return null
      return null;
    }
  
    if (f.size < 5 * 1024 * 1024) { // <5MB full hash
      const buffer = await f.arrayBuffer();
      const hashBuffer = await crypto.subtle.digest('SHA-256', buffer);
      return Array.from(new Uint8Array(hashBuffer)).map(b => b.toString(16).padStart(2, '0')).join('');
    } else { // Partial hash for large files
      const chunkSize = 64 * 1024;
      const start = f.slice(0, chunkSize);
      const end = f.slice(f.size - chunkSize);
      const buffers = await Promise.all([start.arrayBuffer(), end.arrayBuffer()]);
      const hashBuffer = await crypto.subtle.digest('SHA-256', new Blob(buffers));
      return Array.from(new Uint8Array(hashBuffer)).map(b => b.toString(16).padStart(2, '0')).join('');
    }
  };
  
  
  

  const saveChanges = async () => {
    try {
      setIsSaving(true);
      setSaveProgress(0);
      setSaveStatus('Starting save process...');
      
      if (!user?.agent?.id) throw new Error('No agent ID found');
      const token = await getToken();
      if (!token) throw new Error('No auth token found');
  
      // 1️⃣ Update agent metadata
      setSaveProgress(10);
      setSaveStatus('Updating agent metadata...');
      
      const updateData = {
        name: user.agent.name || 'My Agent',
        description: user.agent.description || 'AI agent built with Agent Builder',
        systemPrompt: user.agent.systemPrompt || 'You are a helpful AI assistant.',
        builtInContext: localContextData
      };
  
      const agentResponse = await fetch(`/api/agents`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(updateData)
      });
  
      if (!agentResponse.ok) throw new Error(`Agent update failed: ${await agentResponse.text()}`);
      console.log('Agent metadata updated successfully');
  
      // 2️⃣ Compute hashes
      setSaveProgress(20);
      setSaveStatus('Computing file hashes...');
      
      const oldHashes = {};
      if (originalUploadedFiles?.length) {
        for (const f of originalUploadedFiles) {
          if (!f) continue;
          oldHashes[f.name] = await computeFileHash(f.file || f);
        }
      }

      const newHashes = {};
      if (uploadedFiles?.length) {
        for (const f of uploadedFiles) {
          if (!f) continue;
          newHashes[f.name] = await computeFileHash(f.file || f);
        }
      }

      // 3️⃣ Determine deletions
      setSaveProgress(30);
      setSaveStatus('Determining file changes...');
      
      const filesToDelete = originalUploadedFiles.filter(
        f => !uploadedFiles.find(u => u.name === f.name) || newHashes[f.name] !== oldHashes[f.name]
      );
  
      if (filesToDelete.length > 0) {
        setSaveProgress(40);
        setSaveStatus(`Deleting ${filesToDelete.length} files from vector store...`);
        console.log(`Deleting ${filesToDelete.length} files from vector store...`);
        await fetch(`/api/agents/files`, {
          method: 'DELETE',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          body: JSON.stringify({ files: filesToDelete.map(f => f.name) })
        });
      }
  
      // 4️⃣ Determine uploads (new or changed files)
      const filesToUpload = uploadedFiles.filter(
        f => !originalUploadedFiles.find(of => of.name === f.name) || newHashes[f.name] !== oldHashes[f.name]
      );
  
      if (filesToUpload.length > 0) {
        setSaveProgress(50);
        setSaveStatus(`Uploading ${filesToUpload.length} files and processing...`);
        console.log(`Uploading ${filesToUpload.length} new/updated files...`);
        
        // Prepare files for upload - don't include File objects (can't be JSON stringified)
        const filesPayload = filesToUpload.map(f => ({
          name: f.name,
          content: f.content,  // <- send the text content
          size: f.size,
          type: f.type
        }));
        
        console.log('Sending files to server:', filesPayload.map(f => ({ name: f.name, size: f.size, contentLength: f.content?.length })));
        
        const uploadResponse = await fetch(`/api/agents/files`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          body: JSON.stringify({ 
            files: filesPayload
          })
        });
        
        console.log('Upload response status:', uploadResponse.status);
        
        if (!uploadResponse.ok) {
          const errorText = await uploadResponse.text();
          console.error('File upload failed:', errorText);
          throw new Error(`File upload failed: ${errorText}`);
        }
        
        const responseData = await uploadResponse.json();
        console.log('Upload response data:', responseData);
        
        setSaveProgress(90);
        setSaveStatus('Finalizing changes...');
      }
  
      // 5️⃣ Update local state
      setSaveProgress(100);
      setSaveStatus('Saving completed successfully!');
      
      setHasUnsavedChanges(false);
      setOriginalContextData(localContextData);
      setOriginalUploadedFiles([...uploadedFiles]);
      onFilesChange?.([...uploadedFiles.filter(f => f.source !== 'website_scan')]);
  
      console.log('All changes saved successfully');
      
      // Clear progress after a short delay
      setTimeout(() => {
        setIsSaving(false);
        setSaveProgress(0);
        setSaveStatus('');
      }, 2000);
      
    } catch (error) {
      console.error('Error saving changes:', error);
      setSaveStatus(`Error: ${error.message}`);
      alert(`Error saving changes: ${error.message}`);
      setIsSaving(false);
      setSaveProgress(0);
    }
  };
  
  
  
  
  

  const cancelChanges = () => {
    setLocalContextData(originalContextData);
    setContextData(originalContextData);
    setUploadedFiles([...originalUploadedFiles]);
    setHasUnsavedChanges(false);
    
    // Notify parent component to revert file changes
    if (onFilesChange) {
      onFilesChange([...originalUploadedFiles.filter(f => f.source !== 'website_scan')]);
    }
  };

  const handleTextInput = (e) => {
    const value = e.target.value;
    setLocalContextData(value);
    setContextData(value); // Also update parent for consistency
    setHasUnsavedChanges(true);
  };



  if (isLoading) {
    return (
      <div className="context-upload">
        <div className="loading-section">
          <h2>Loading your agent...</h2>
          <p>Please wait while we load your existing context and files.</p>
        </div>
      </div>
    );
  }

  if (!user?.agent) {
    return (
      <div className="context-upload">
        <div className="loading-message">
          <p>Loading your agent's context...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="context-upload" data-theme="dark">
      {isLoading ? (
        <div className="loading-section">
          <h2>Loading your agent...</h2>
          <p>Please wait while we load your existing context and files.</p>
        </div>
      ) : (
        <div className="upload-section">
          
          <div className="upload-methods">
            {!showTextOnly && (
              <div className="method-section">
                <h3>Upload Files</h3>
                <p style={{ marginTop: '8px', marginBottom: '16px', color: 'var(--text-muted)', fontSize: '16px' }}>Uploaded text files are incorporated into your agent's knowledge base.</p>
              <div
                {...getRootProps()}
                className={`dropzone ${isDragActive ? 'drag-active' : ''}`}
              >
                <input {...getInputProps()} />
                <Upload className="upload-icon" />
                <p>
                  {isDragActive
                    ? 'Drop the files here...'
                    : 'Drag & drop files here, or click to select'}
                </p>
                <span className="file-types">Supports: TXT, MD (text files only)</span>
              </div>

              {uploadedFiles.filter(file => file.source !== 'website_scan').length > 0 && (
                <div className="uploaded-files">
                  <h4>Uploaded Files:</h4>
                  {uploadedFiles.filter(file => file.source !== 'website_scan').map(file => (
                    <div key={file.id} className="file-item">
                      <FileText className="file-icon" />
                      <div className="file-info">
                        <span className="file-name">{file.name}</span>
                        <span className="file-size">{(file.size / 1024).toFixed(1)} KB</span>
                      </div>
                      <button
                        onClick={() => removeFile(file.id)}
                        className="remove-file"
                        title="Remove this file from context"
                      >
                        <X size={16} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              </div>
            )}

            {!showFilesOnly && (
              <div className="method-section">
                <h3>{showTextOnly ? 'Instruct Your Agent on How to Answer' : 'Or Write Instructions Directly'}</h3>
                {showTextOnly && (
                  <>
                    <p style={{ marginTop: '0px', marginBottom: '16px', color: 'var(--text-muted)', fontSize: '16px' }}>Use this section to set your agent's tone and style, e.g. "Keep your responses to less than 100 words" or "Always keep the conversation upbeat".</p>
                  </>
                )}
              <textarea
                value={localContextData || ''}
                onChange={handleTextInput}
                placeholder="Write instructions, tips, or knowledge you want your agent to remember... (This is separate from uploaded files)"
                className="text-input"
                rows={8}
              />
              {localContextData && (
                <div className="text-status">
                  <CheckCircle className="status-icon" />
                  <span>Text context ready ({localContextData.length} characters)</span>
                </div>
              )}
              </div>
            )}
          </div>
          
          {/* Save/Cancel Buttons */}
          <div className="action-buttons">
            {hasUnsavedChanges && !isSaving && (
              <div className="unsaved-changes-indicator">
                <span> You have unsaved changes</span>
              </div>
            )}
            
            {isSaving && (
              <div className="save-progress">
                <div className="progress-bar-container">
                  <div className="progress-bar">
                    <div 
                      className="progress-fill" 
                      style={{ width: `${saveProgress}%` }}
                    ></div>
                  </div>
                  <span className="progress-text">{saveProgress}%</span>
                </div>
                <div className="progress-status">{saveStatus}</div>
              </div>
            )}
            
            <div className="button-group">
              <button
                onClick={cancelChanges}
                className="cancel-button"
                disabled={!hasUnsavedChanges || isSaving}
              >
                Cancel
              </button>
              <button
                onClick={saveChanges}
                className="save-button"
                disabled={!hasUnsavedChanges || isSaving}
              >
                {isSaving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContextUpload;
